import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PLANNER_THUMBNAIL_UPLOAD_ENDPOINT } from "../../../_actions/endpoints";
import * as PostMediaTypes from "../PostMedia/PostMediaTypes.constants";
import { showToast } from "../../../_utils/showToast";
import { createVideoThumbnail } from "./utils";

const PostMediaEditThumbModal = ({ item, onSelect, onClose }) => {
  const { t } = useTranslation(["planner", "common"]);
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [selectedTime, setSelectedTime] = useState(0);
  const thumbSize = 300;

  const saveImage = useCallback(async (base64) => {
    const ext = base64.split(";")[0].split("/")[1];
    const response = await axios.post(PLANNER_THUMBNAIL_UPLOAD_ENDPOINT, {
      name: `video-thumb.${ext}`,
      data: base64,
    });

    if (response.data?.status) {
      return response.data?.url || "";
    }

    return "";
  }, []);

  const handleSave = useCallback(async () => {
    setLoading(true);

    try {
      const base64 = await createVideoThumbnail(
        item.url,
        selectedTime,
        thumbSize,
        thumbSize
      );
      const thumbUrl = await saveImage(base64);

      onSelect?.(item, thumbUrl, selectedTime * 1000);
      onClose?.();
    } catch (error) {
      showToast({
        description: error.message,
        title: "Error",
        status: "error",
      });
    }

    setLoading(false);
  }, [item, selectedTime, onSelect, onClose, setLoading, saveImage]);

  const handleCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleTimeChange = useCallback(
    (value) => {
      if (videoRef.current) {
        videoRef.current.currentTime = value;
      }
      setSelectedTime(value);
    },
    [videoRef, setSelectedTime]
  );

  const handleLoadVideoMetadata = useCallback(() => {
    setVideoDuration(videoRef.current?.duration || 0);

    if (item.selectedTime) {
      handleTimeChange(item.selectedTime / 1000);
    }
  }, [setVideoDuration, handleTimeChange, item.selectedTime]);

  return (
    <Modal
      autoFocus={false}
      returnFocusOnClose={false}
      isOpen
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("post_media.edit_thumb.modal.title")}</ModalHeader>
        <ModalBody>
          <Box paddingBottom="2" position="relative">
            <Box
              fontSize="sm"
              background="whiteAlpha.100"
              paddingX="3"
              paddingY="2"
              marginX="-3"
              marginBottom="4"
            >
              {t("post_media.edit_thumb.notice")}
            </Box>
            <Box fontSize="sm" marginBottom="4">
              {t("post_media.edit_thumb.description")}
            </Box>
            <Box>
              <Box
                as="video"
                ref={videoRef}
                src={item.url}
                controls={false}
                preload="auto"
                onLoadedMetadata={handleLoadVideoMetadata}
                width="100%"
                height="auto"
              />
            </Box>
            <Box marginTop="1">
              <Slider
                variant="solid"
                value={selectedTime}
                min={0}
                max={videoDuration}
                step={0.001}
                onChange={handleTimeChange}
                isDisabled={!videoRef.current || loading}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </Box>
            <Flex
              marginTop="4"
              paddingTop="4"
              justifyContent="flex-end"
              borderTop="1px solid"
              borderColor="whiteAlpha.300"
            >
              <ButtonGroup size="sm">
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  isDisabled={loading}
                >
                  {t("cancel", { ns: "common" })}
                </Button>
                <Button
                  variant="solid"
                  onClick={handleSave}
                  isDisabled={!videoRef.current || loading}
                  isLoading={loading}
                >
                  {t("post_media.edit_thumb.save")}
                </Button>
              </ButtonGroup>
            </Flex>
          </Box>
        </ModalBody>
        <ModalCloseButton isDisabled={loading} />
      </ModalContent>
    </Modal>
  );
};

PostMediaEditThumbModal.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([PostMediaTypes.VIDEO]).isRequired,
    url: PropTypes.string.isRequired,
    selectedTime: PropTypes.number,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

PostMediaEditThumbModal.defaultProps = {};

export default PostMediaEditThumbModal;
